import * as babylon from "@babylonjs/core";
import lodash from "lodash";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

import {
  materialForColor,
  recolorMaterial,
} from "../../assetBuilders/materials";
import type * as components from "../../components";
import { BabylonContext, BabylonState, Entity } from "../../state";

/** Sets the state's entities based on the incoming ThreeDPanelState */
export function updateEntities(state: BabylonState, next: schema.PanelData) {
  // Remove entities that need to be removed.
  for (const entity of Object.values(state.entities)) {
    if (!next.entities[entity.id]) {
      // Dispose of all components on the entity.
      disposePosition(entity);
      disposeMesh(entity);
      disposePath(entity);
      disposeVectorField(entity);
      delete state.entities[entity.id];
    }
  }

  // Update or create entities that need to be updated or created.
  for (const entityId of Object.keys(next.entities)) {
    const nextSpec = next.entities[entityId];
    let entity = state.entities[entityId];

    // Identify what change needs to happen.
    if (!entity) {
      // Create the entity if it doesn't exist.
      entity = {
        id: entityId,
      };
      state.entities[entityId] = entity;
    }
    updatePosition(entity, nextSpec.position);
    updateMesh(entity, nextSpec.mesh, state.context);
    updatePath(entity, nextSpec.path);
    updateVectorField(entity, nextSpec.vectorField);
  }

  // Invalidate the third-person zoom cache. `updateEntities` is the
  // funnel for every panel-state-driven mutation of `state.entities`
  // — adds, removes, mesh rescales — so a bump here covers the cases
  // that change the scene-union bounding box. Path-mesh creation
  // happens later in `reloadStalePaths`, which bumps independently.
  state.sceneConfig.entityGeneration =
    (state.sceneConfig.entityGeneration ?? 0) + 1;
}

function updatePosition(entity: Entity, spec: schema.Position | undefined) {
  if (lodash.isEqual(entity.position?.spec, spec)) {
    return;
  }
  // Always reset the position if a change has occurred.
  disposePosition(entity);
  // If the entity's position was traced, mark the trace as dirty and abort
  // any ongoing mesh construction.
  if (entity.path) {
    entity.path.load.state = "stale";
  }
  if (entity.vectorField) {
    entity.vectorField.meshStale = true;
  }
  // Remove deleted positions
  if (!spec) {
    return;
  }

  entity.position = {
    spec,
    current: { logtime: 0n, vector: babylon.Vector3.Zero() },
    mean: babylon.Vector3.Zero(),
    cache: {
      load: {
        abortController: new AbortController(),
        state: "stale",
      },
      logtimes: [0n],
      allPoints: [babylon.Vector3.Zero()],
    },
  };

  // Pre-set any constant vector components from the spec.
  if (typeof spec.right === "number") {
    const x = spec.right * spec.rightScale;
    entity.position.current.vector.x = x;
    entity.position.mean.x = x;
  }
  if (typeof spec.forward === "number") {
    const z = spec.forward * spec.forwardScale;
    entity.position.current.vector.z = z;
    entity.position.mean.z = z;
  }
  if (typeof spec.up === "number") {
    const y = spec.up * spec.upScale;
    entity.position.current.vector.y = y;
    entity.position.mean.y = y;
  }
}

function disposePosition(entity: Entity) {
  entity.position?.cache.load.abortController?.abort();
  if (entity.path) {
    entity.path.load.state = "stale";
  }
  if (entity.vectorField) {
    entity.vectorField.meshStale = true;
  }
  delete entity.position;
}

function updateMesh(
  entity: Entity,
  nextSpec: schema.Mesh | undefined,
  context: BabylonContext,
) {
  if (lodash.isEqual(entity.mesh?.spec, nextSpec)) {
    return;
  }
  const rescaleMesh = (mesh: babylon.Mesh, scale: number) => {
    mesh.scaling = new babylon.Vector3(scale, scale, scale);
  };

  if (entity.mesh && nextSpec) {
    const currentSpec = entity.mesh.spec;
    if (currentSpec.type === nextSpec.type) {
      // If the render mesh's type is unchanged, then we don't have to dispose of it.
      // Instead, mutate its scaling and material colors.
      if (currentSpec.color !== nextSpec.color) {
        // Recolor the mesh's material.
        recolorMaterial(entity.mesh.material, nextSpec.color);
      }
      if (nextSpec.type === "box") {
        rescaleMesh(entity.mesh.mesh, nextSpec.size);
      }
      if (nextSpec.type === "sphere") {
        rescaleMesh(entity.mesh.mesh, nextSpec.radius);
      }
      entity.mesh.spec = nextSpec;
      return;
    }
  }

  // Otherwise, always reset this component.
  disposeMesh(entity);
  if (!nextSpec) {
    return;
  }
  const material = materialForColor(
    `${entity.id}.material`,
    nextSpec.color,
    context.scene,
  );
  let mesh: babylon.Mesh;
  switch (nextSpec.type) {
    case "sphere":
      mesh = babylon.MeshBuilder.CreateSphere(entity.id, { diameter: 2 });
      rescaleMesh(mesh, nextSpec.radius);
      break;
    case "box":
      mesh = babylon.MeshBuilder.CreateBox(entity.id, { size: 1 });
      rescaleMesh(mesh, nextSpec.size);
      break;
  }
  mesh.material = material;
  context.scene.addMesh(mesh);
  entity.mesh = {
    spec: nextSpec,
    material,
    mesh,
  };
}

function disposeMesh(entity: Entity) {
  entity.mesh?.material?.dispose();
  entity.mesh?.mesh.dispose();
  delete entity.mesh;
}

function updatePath(entity: Entity, spec: schema.Path | undefined) {
  if (lodash.isEqual(entity.path?.spec, spec)) {
    return;
  }
  // Always reset the path if a change has occurred.
  disposePath(entity);
  if (!spec) {
    return;
  }

  entity.path = {
    spec,
    mesh: undefined,
    load: {
      state: "stale",
    },
  };
  // The stale path mesh will be built once the entity's position data is loaded.
}

function disposePath(entity: Entity) {
  entity.path?.mesh?.dispose();
  entity.path?.material?.dispose();
  delete entity.path;
}

/**
 * Returns the subset of VectorField spec properties that affect the underlying
 * data (axis bindings and scale factors). Changes to these require a full cache
 * reload. All other properties (color, lineWidth, stride, displayMode, gradient
 * settings) are display-only and can be applied by rebuilding the mesh alone.
 */
function vectorFieldDataSpec(spec: schema.VectorField) {
  return {
    right: spec.right,
    rightScale: spec.rightScale,
    up: spec.up,
    upScale: spec.upScale,
    forward: spec.forward,
    forwardScale: spec.forwardScale,
  };
}

function updateVectorField(
  entity: Entity,
  spec: schema.VectorField | undefined,
) {
  if (lodash.isEqual(entity.vectorField?.spec, spec)) {
    return;
  }

  // If removing the vector field, dispose everything.
  if (!spec) {
    disposeVectorField(entity);
    return;
  }

  // If the vector field already exists and only display properties changed
  // (not the axis bindings / scales), keep the cached data and just rebuild
  // the mesh with the new visual settings.
  if (
    entity.vectorField &&
    lodash.isEqual(
      vectorFieldDataSpec(entity.vectorField.spec),
      vectorFieldDataSpec(spec),
    )
  ) {
    disposeVectorFieldMeshes(entity.vectorField);
    entity.vectorField.gradient = undefined;
    entity.vectorField.spec = spec;
    entity.vectorField.meshStale = true;
    return;
  }

  // Data-affecting properties changed — full teardown and reload.
  disposeVectorField(entity);

  entity.vectorField = {
    spec,
    mesh: undefined,
    currentMesh: undefined,
    current: babylon.Vector3.Zero(),
    cache: {
      logtimes: [],
      allVectors: [],
      load: {
        abortController: new AbortController(),
        state: "stale",
      },
    },
    meshStale: true,
  };
}

/** Disposes the mesh and currentMesh (and their materials) on a vector field. */
function disposeVectorFieldMeshes(vf: components.VectorField) {
  vf.mesh?.material?.dispose();
  vf.mesh?.dispose();
  vf.currentMesh?.material?.dispose();
  vf.currentMesh?.dispose();
  vf.mesh = undefined;
  vf.currentMesh = undefined;
}

function disposeVectorField(entity: Entity) {
  if (entity.vectorField) {
    entity.vectorField.cache.load.abortController?.abort();
    disposeVectorFieldMeshes(entity.vectorField);
  }
  delete entity.vectorField;
}
